import React, { useState, useEffect, useCallback } from "react";
import { Container, Section } from "components/shared";
import { getFieldValue } from "@sitecore-jss/sitecore-jss-react";
import styled from "styled-components";
import { AspectRatio } from "react-aspect-ratio";
import useBreakpoint from "utils/hooks/useBreakpoint";
import {
  useUiValue,
  openGalleryModal,
  closeGalleryModal,
} from "stores/uiContext";
import { useSitecoreContext } from "stores/sitecoreContext";
import templateIds from "utils/templateIds";
import useFavorites from "utils/hooks/useFavorites";
import {
  videoTranscriptFileUrl,
  videoTranscriptFileExtension,
} from "utils/videoTranscriptFileUrl";
import { scrollToSection } from "utils/scrollTo";
import breakpoints from "utils/breakpoints";
import { isValidTitle } from "utils/validateTitle";
import { isValidUrl } from "utils/validateUrlSrc";

import loadable from "@loadable/component";
const ExtendedGallery = loadable(() =>
  import("components/components/organisms/ExtendedGallery")
);
const ExtendedGalleryModal = loadable(() =>
  import("components/components/organisms/GalleryModal/ExtendedGalleryModal")
);
const Heading = loadable(() => import("components/components/atoms/Heading"));
const Copy = loadable(() => import("components/components/atoms/Copy"));

const StyledSection = styled(Section)`
  ${({ noPaddingTop }) =>
    noPaddingTop &&
    `
    padding-top: 0 !important;
  `}
  ${({ minHeight }) =>
    minHeight &&
    `  min-height:${minHeight}px;
  `}
`;

const StyledCopy = styled(Copy)`
  @media ${breakpoints.lg} {
    width: 55rem;
  }
`;

const filterCtas = (media) => {
  if (media && media.length > 0) {
    const ctas = [
      { key: "tour", text: "360 Tours" },
      { key: "image", text: "Photos" },
      { key: "video", text: "Videos" },
    ];
    const cleanMedia = media.filter((item) => {
      if (item && item.type) {
        if (item.type === "tour") {
          return (
            item.src &&
            item.src.includes("iframe") &&
            !/\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(item.src)
          );
        }
        if (item.type === "image") {
          return item.src && isValidUrl(item.src);
        }
        if (["internal", "external"].includes(item.type)) {
          return (
            item.src &&
            isValidUrl(item.src) &&
            !/\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(item.src)
          );
        }
      }
      return false;
    });
    const filterCtas = ctas.filter((cta) => {
      const itemIndex = cleanMedia.findIndex(
        (i) =>
          i.type === cta.key ||
          (cta.key === "video" && ["external", "internal"].includes(i.type))
      );
      return itemIndex !== -1;
    });
    let sortCtas = [];
    const hasVideo = filterCtas.find((e) => e.key === "video");
    if (hasVideo) sortCtas.push(hasVideo);
    const hasTour = filterCtas.find((e) => e.key === "tour");
    if (hasTour) sortCtas.push(hasTour);
    const hasImage = filterCtas.find((e) => e.key === "image");
    if (hasImage) sortCtas.push(hasImage);
    return sortCtas;
  }
  return [];
};

const getGallery = (media, filter) =>
  !filter
    ? media
    : media.filter((e) =>
        filter === "video"
          ? ["internal", "external"].includes(e.type)
          : e.type.includes(filter)
      );

const isDesingStudioPage = () => {
  if (
    typeof window !== "undefined" &&
    window.location &&
    window.location.pathname
  ) {
    const pathname = window.location.pathname;
    const splitpath = pathname.split("/");
    const getlastStr = splitpath[splitpath.length - 1];
    if (getlastStr.includes("design-studio")) {
      return "Design Studio options";
    }
  }
  return "";
};

const ExtendedGallerySection = ({
  fields,
  preSellingHome = false,
  messageFlag = "",
}) => {
  const media = getFieldValue(fields, "media") || [];
  const ctas = getFieldValue(fields, "ctas");
  const showFavorites = getFieldValue(fields, "showFavorites");
  const showShare = getFieldValue(fields, "showShare");
  const availableDateMessage = preSellingHome
    ? ""
    : getFieldValue(fields, "availableDateMessage");
  const modelHome = getFieldValue(fields, "modelHome");
  const homeSpecs = getFieldValue(fields, "homeSpecs") || [];
  const buttonCta = getFieldValue(fields, "buttonCta");
  const title = getFieldValue(fields, "title") || isDesingStudioPage();
  const copy = getFieldValue(fields, "copy");

  const [{ showGalleryModal }, dispatch] = useUiValue();
  const { favoritesList, saveFavorite, deleteFavorite } = useFavorites();

  const { templateId } = useSitecoreContext();

  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["sm", "xs"].includes(breakpoint);

  const [filter, setFilter] = useState();
  const [position, setPosition] = useState(0);
  let filteredMedia = getGallery(media, filter);
  const filteredCtas = filterCtas(media);

  const handleFilterChange = useCallback(
    (value) => {
      switch (value) {
        case "360 Tours":
          setFilter(filter !== "tour" ? "tour" : undefined);
          break;
        case "Videos":
          setFilter(filter !== "video" ? "video" : undefined);
          break;
        case "Photos":
          setFilter(filter !== "image" ? "image" : undefined);
          break;
      }
    },
    [filter]
  );

  const handleOnPositionChange = useCallback(
    (value) => {
      setPosition(value);
    },
    [position]
  );

  const handleOpenModal = useCallback(() => {
    //if (!isMobile) {
    dispatch(openGalleryModal());
    setTimeout(() => {
      const element = document.getElementById("extendedGalleryModal");
      if (element) {
        element.focus();
      }
    }, 1000);
    //}
  }, []);

  const handleCloseModal = useCallback(() => {
    dispatch(closeGalleryModal());
    setFilter("");
  }, []);

  const handleCtaClick = useCallback(() => {
    dispatch(closeGalleryModal());
    scrollToSection("contact");
  }, []);
  const [minHeight, setMinHeight] = useState(280);
  useEffect(() => {
    try {
      let viewPortWidth;
      if (typeof window.innerWidth != "undefined") {
        viewPortWidth = window.innerWidth;
      }
      let newHeight = isMobile ? 280 : 650;
      if (isMobile) {
        newHeight = (280 / 375) * viewPortWidth;
      } else {
        newHeight = (650 / 1200) * viewPortWidth;
      }
      //console.log("img.onload newHeight", newHeight);
      setMinHeight(Math.ceil(newHeight));
    } catch (e) {
      console.log(e);
    }
  }, []);

  if (!media || !media.length) {
    return null;
  }

  const showHeading = ![
    templateIds.PlanPage,
    templateIds.QMIPage,
    templateIds.MpcPage,
  ].includes(templateId);

  const renderVideoDescription = () =>
    filteredMedia
      .filter((m) => m.type === "internal" || m.type === "external")
      .filter(
        (m) =>
          m.alt && m.videoAltTranscriptFile && m.videoAltTranscriptFile.link
      )
      .map((m, i) => (
        <span
          key={i}
          className="richtext text-left text-md leading-7 font-graphie text-mattamy-blue"
        >
          {"Transcript: "}
          <a
            href={videoTranscriptFileUrl(m.videoAltTranscriptFile)}
            download={
              m.videoAltTranscriptFile.name +
              videoTranscriptFileExtension(m.videoAltTranscriptFile.extension)
            }
            className="underline"
            aria-label={"Download " + m.alt + " transcript"}
          >
            {m.alt}
          </a>
        </span>
      ));

  return (
    <StyledSection
      id="gallery"
      className="pt-5 lg:pt-0"
      noPaddingTop={!title || !showHeading}
      tabIndex={"-1"}
      //minHeight={minHeight}
    >
      <Container className="flex flex-col items-center" noPadding>
        {isValidTitle(title) && showHeading && (
          <Heading as="h2" className={"text-center pb-2"}>
            {title}
          </Heading>
        )}
        {copy && showHeading && (
          <StyledCopy className="md:text-lg text-center pb-6 px-8">
            {copy}
          </StyledCopy>
        )}
        <AspectRatio
          ratio={isMobile ? "375/280" : "1200/650"}
          className="w-full"
        >
          <ExtendedGallery
            className="w-full"
            carousel={filteredMedia}
            ctas={ctas}
            showFavorites={showFavorites}
            showShare={showShare}
            availableDateMessage={availableDateMessage}
            modelHome={modelHome}
            onOpenModal={handleOpenModal}
            onChange={handleOnPositionChange}
            handleFilterChange={handleFilterChange}
            filter={filter}
            favoritesList={favoritesList}
            saveFavorite={saveFavorite}
            deleteFavorite={deleteFavorite}
            title={title}
            messageFlag={messageFlag}
            filteredCtas={filteredCtas}
          />
        </AspectRatio>
        {filteredMedia && (
          <div className="w-full mt-2 flex flex-col">
            {renderVideoDescription()}
          </div>
        )}
        {showGalleryModal && (
          <ExtendedGalleryModal
            isOpen={showGalleryModal}
            selectedPostion={position}
            handleFilterChange={handleFilterChange}
            media={filteredMedia}
            filter={filter}
            ctas={ctas}
            showFavorites={showFavorites}
            onClose={handleCloseModal}
            homeSpecs={homeSpecs.homeSpecs}
            buttonCta={buttonCta}
            title={title}
            handleCtaClick={handleCtaClick}
            favoritesList={favoritesList}
            saveFavorite={saveFavorite}
            deleteFavorite={deleteFavorite}
          />
        )}
      </Container>
    </StyledSection>
  );
};

export default ExtendedGallerySection;
